import { NonAIDataResult, TeamMemberApplications } from '../types';
import { 
  arrayToCSV, 
  downloadCSV, 
  downloadPDF, 
  createPDF, 
  addTableToPDF,
  addPageBreakIfNeeded,
  formatDecimalHoursForDisplay,
  generateFilename,
  getDataModeLabel,
  getPeriodLabel
} from './exportUtils';
import { formatDecimalHours } from './personalizedDataService';

/**
 * Export service for Non-AI Apps board (NonAIApplicationsTable component)
 */

export interface NonAIAppsExportOptions {
  dataMode: string;
  reportingPeriod: string;
  selectedWeek: string;
}

/**
 * Prepare team member non-AI data for CSV export
 */
const prepareNonAIDataForCSV = (teamMembers: TeamMemberApplications[]): any[] => {
  return teamMembers.map((member, index) => {
    // Calculate email time
    const emailTime = Object.entries(member.applications)
      .filter(([, app]) => app.category.toLowerCase().includes('email'))
      .reduce((sum, [, app]) => sum + app.hours, 0);

    // Get top category
    const categoryTotals = member.applicationsByCategory;
    const topCategory = Object.keys(categoryTotals).length > 0 
      ? Object.keys(categoryTotals).reduce((a, b) => 
          categoryTotals[a].reduce((sum, app) => sum + app.hours, 0) > 
          categoryTotals[b].reduce((sum, app) => sum + app.hours, 0) ? a : b
        )
      : 'None';
    
    const topCategoryTime = Object.keys(categoryTotals).length > 0 
      ? categoryTotals[topCategory].reduce((sum, app) => sum + app.hours, 0)
      : 0;

    return {
      Rank: index + 1,
      'Team Member': member.name || member.email,
      'Email': member.email,
      'Total Non-AI Time': formatDecimalHours(member.totalNonAIHours),
      'Email Time': emailTime > 0 ? formatDecimalHours(emailTime) : '-',
      'Top Category': topCategory,
      'Top Category Time': topCategoryTime > 0 ? formatDecimalHours(topCategoryTime) : '-',
      'Application Count': Object.keys(member.applications).length,
      'All Applications': Object.entries(member.applications)
        .sort(([,a], [,b]) => b.hours - a.hours)
        .map(([appName, appData]) => `${appName} (${formatDecimalHours(appData.hours)}, ${appData.category})`)
        .join('; ')
    };
  });
};

/**
 * Prepare top applications data for CSV export
 */
const prepareTopApplicationsForCSV = (nonAIData: NonAIDataResult): any[] => {
  return nonAIData.topApplications.map((app, index) => ({
    Rank: index + 1,
    'Application Name': app.applicationName,
    'Category': app.category,
    'Total Time': formatDecimalHours(app.totalHours),
    'User Count': app.totalUsers,
    'Average Time per User': formatDecimalHours(app.totalHours / app.totalUsers),
    'Usage Percentage': app.percentage
  }));
};

/**
 * Export Non-AI Apps data to CSV
 */
export const exportNonAIAppsToCSV = (
  nonAIData: NonAIDataResult,
  options: NonAIAppsExportOptions
): void => {
  if (!nonAIData || nonAIData.teamMemberApplications.length === 0) {
    alert('No Non-AI Apps data available to export');
    return;
  }

  try {
    // Prepare team member data
    const teamMemberData = prepareNonAIDataForCSV(nonAIData.teamMemberApplications);
    const teamMemberCSV = arrayToCSV(teamMemberData);
    
    // Prepare top applications data
    const topAppsData = prepareTopApplicationsForCSV(nonAIData);
    const topAppsCSV = arrayToCSV(topAppsData);
    
    // Combine both datasets with headers
    const combinedCSV = [
      '# Non-AI Applications - Team Member Usage',
      teamMemberCSV,
      '',
      '# Top Non-AI Applications',
      topAppsCSV
    ].join('\n');
    
    const dataMode = getDataModeLabel(options.dataMode);
    const period = getPeriodLabel(options.reportingPeriod);
    
    const filename = generateFilename(
      `non_ai_apps_${dataMode}_${period}_${options.selectedWeek}`,
      'csv'
    );
    
    downloadCSV(combinedCSV, filename);
    console.log('✅ [EXPORT] Non-AI Apps CSV exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export Non-AI Apps CSV:', error);
    alert('Failed to export CSV. Please try again.');
  }
};

/**
 * Export Non-AI Apps data to PDF
 */
export const exportNonAIAppsToPDF = (
  nonAIData: NonAIDataResult,
  options: NonAIAppsExportOptions
): void => {
  if (!nonAIData || nonAIData.teamMemberApplications.length === 0) {
    alert('No Non-AI Apps data available to export');
    return;
  }

  try {
    const dataMode = getDataModeLabel(options.dataMode);
    const period = getPeriodLabel(options.reportingPeriod);
    
    const title = `Non-AI Applications Usage - ${dataMode} (${period})`;
    const pdf = createPDF(title, 'landscape');

    // Add summary statistics
    let currentY = 40;
    pdf.setFontSize(12);
    pdf.text(`Week: ${options.selectedWeek}`, 20, currentY);
    currentY += 10;
    
    pdf.setFontSize(10);
    const totalHours = nonAIData.teamMemberApplications.reduce((sum, member) => sum + member.totalNonAIHours, 0);
    const totalApps = new Set(nonAIData.teamMemberApplications.flatMap(member => Object.keys(member.applications))).size;
    
    pdf.text(`Total Team Members: ${nonAIData.teamMemberApplications.length}`, 20, currentY);
    currentY += 8;
    pdf.text(`Total Non-AI Time: ${formatDecimalHours(totalHours)}`, 20, currentY);
    currentY += 8;
    pdf.text(`Unique Applications: ${totalApps}`, 20, currentY);
    currentY += 15;

    // Team Member Usage Table
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Team Member Non-AI Usage', 20, currentY);
    currentY += 10;

    const memberHeaders = [
      'Rank',
      'Team Member',
      'Total Time',
      'Email Time',
      'Top Category',
      'Apps'
    ];

    const memberTableData = nonAIData.teamMemberApplications.map((member, index) => {
      // Calculate email time
      const emailTime = Object.entries(member.applications)
        .filter(([, app]) => app.category.toLowerCase().includes('email'))
        .reduce((sum, [, app]) => sum + app.hours, 0);

      // Get top category
      const categoryTotals = member.applicationsByCategory;
      const topCategory = Object.keys(categoryTotals).length > 0 
        ? Object.keys(categoryTotals).reduce((a, b) => 
            categoryTotals[a].reduce((sum, app) => sum + app.hours, 0) > 
            categoryTotals[b].reduce((sum, app) => sum + app.hours, 0) ? a : b
          )
        : 'None';

      return [
        String(index + 1),
        member.name || member.email,
        formatDecimalHours(member.totalNonAIHours),
        emailTime > 0 ? formatDecimalHours(emailTime) : '-',
        topCategory,
        String(Object.keys(member.applications).length)
      ];
    });

    currentY = addTableToPDF(pdf, memberHeaders, memberTableData, currentY, {
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 15 }, // Rank
        1: { cellWidth: 40 }, // Team Member
        2: { cellWidth: 25 }, // Total Time
        3: { cellWidth: 25 }, // Email Time
        4: { cellWidth: 30 }, // Top Category
        5: { cellWidth: 15 }  // Apps
      }
    }) + 20;

    // Top Applications Table
    if (nonAIData.topApplications.length > 0) {
      currentY = addPageBreakIfNeeded(pdf, currentY, 60);
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Top Non-AI Applications', 20, currentY);
      currentY += 10;

      const appHeaders = [
        'Rank',
        'Application',
        'Category',
        'Total Time',
        'Users',
        'Avg Time/User'
      ];

      const appTableData = nonAIData.topApplications.slice(0, 20).map((app, index) => [
        String(index + 1),
        app.applicationName,
        app.category,
        formatDecimalHours(app.totalHours),
        String(app.totalUsers),
        formatDecimalHours(app.totalHours / app.totalUsers)
      ]);

      addTableToPDF(pdf, appHeaders, appTableData, currentY, {
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 15 }, // Rank
          1: { cellWidth: 45 }, // Application
          2: { cellWidth: 35 }, // Category
          3: { cellWidth: 25 }, // Total Time
          4: { cellWidth: 20 }, // Users
          5: { cellWidth: 25 }  // Avg Time/User
        }
      });
    }

    const filename = generateFilename(
      `non_ai_apps_${dataMode}_${period}_${options.selectedWeek}`,
      'pdf'
    );
    
    downloadPDF(pdf, filename);
    console.log('✅ [EXPORT] Non-AI Apps PDF exported:', filename);
  } catch (error) {
    console.error('❌ [EXPORT] Failed to export Non-AI Apps PDF:', error);
    alert('Failed to export PDF. Please try again.');
  }
};
